import { useEffect, useMemo, useState } from "react";
import api, { formatErr } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  CalendarDays,
  Clock,
  Plus,
  RefreshCw,
  CheckCircle2,
  AlertTriangle,
  UserCheck,
  Search,
} from "lucide-react";

function todayPH() {
  return new Date().toISOString().slice(0, 10);
}

function statusBadge(status) {
  const s = status || "pending_payment";
  const map = {
    pending_payment: "bg-amber-50 text-amber-700 border-amber-200",
    payment_uploaded: "bg-blue-50 text-blue-700 border-blue-200",
    confirmed: "bg-emerald-50 text-emerald-700 border-emerald-200",
    arrived: "bg-slate-100 text-slate-700 border-slate-200",
    cancelled: "bg-red-50 text-red-700 border-red-200",
    no_show: "bg-red-50 text-red-700 border-red-200",
  };
  return map[s] || "bg-slate-100 text-slate-700 border-slate-200";
}

export default function Reservations() {
  const [date, setDate] = useState(todayPH());
  const [reservations, setReservations] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const [form, setForm] = useState({
    patient_id: "",
    doctor_id: "",
    branch_id: "",
    reservation_date: todayPH(),
    reservation_time: "",
    reason: "",
    notes: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = async () => {
    setBusy(true);
    try {
      const [r, p, d, b] = await Promise.all([
        api.get("/reservations", { params: { date } }),
        api.get("/patients"),
        api.get("/doctors"),
        api.get("/hospital/branches").catch(() => ({ data: { branches: [] } })),
      ]);

      setReservations(Array.isArray(r.data) ? r.data : []);
      setPatients(Array.isArray(p.data) ? p.data : []);
      setDoctors(Array.isArray(d.data) ? d.data : []);
      setBranches(Array.isArray(b.data?.branches) ? b.data.branches : []);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date]);

  const filteredPatients = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return patients.slice(0, 30);
    return patients
      .filter((p) =>
        [p.full_name, p.contact_number, p.patient_number]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(needle)
      )
      .slice(0, 30);
  }, [patients, q]);

  const selectedPatient = useMemo(
    () => patients.find((p) => p.id === form.patient_id),
    [patients, form.patient_id]
  );

  const create = async (e) => {
    e.preventDefault();

    if (!form.patient_id) {
      toast.error("Select a patient");
      return;
    }
    if (!form.reservation_date || !form.reservation_time) {
      toast.error("Reservation date and time are required");
      return;
    }

    setBusy(true);
    try {
      await api.post("/reservations", form);
      toast.success("Reservation created");
      setForm({
        patient_id: "",
        doctor_id: "",
        branch_id: "",
        reservation_date: date,
        reservation_time: "",
        reason: "",
        notes: "",
      });
      setQ("");
      await load();
    } catch (e2) {
      toast.error(formatErr(e2?.response?.data?.detail || e2));
    } finally {
      setBusy(false);
    }
  };

  const updateStatus = async (reservation, status) => {
    setBusy(true);
    try {
      await api.put(`/reservations/${reservation.id}`, { status });
      toast.success(`Reservation marked ${status}`);
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  // Confirm = verify the uploaded payment (records who/when + sets payment_status).
  // Falls back to a plain status update for clinics without the payment module.
  const confirmPayment = async (reservation) => {
    setBusy(true);
    try {
      await api.put(`/reservations/${reservation.id}/confirm-payment`);
      toast.success("Payment verified — reservation confirmed");
      await load();
    } catch (e) {
      try {
        await api.put(`/reservations/${reservation.id}`, { status: "confirmed" });
        toast.success("Reservation confirmed");
        await load();
      } catch (e2) {
        toast.error(formatErr(e2?.response?.data?.detail || e2));
      }
    } finally {
      setBusy(false);
    }
  };

  const arrive = async (reservation) => {
    setBusy(true);
    try {
      await api.post(`/reservations/${reservation.id}/arrive`);
      toast.success("Patient checked in from reservation");
      await load();
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail || e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="p-6 md:p-8 max-w-[1500px]" data-testid="reservations-page">
      <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight" style={{ fontFamily: "Work Sans" }}>
            Calendar Reservations
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Create and manage patient reservation slots. Reserved patients can later be checked into the live queue.
          </p>
        </div>

        <div className="flex gap-2">
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="w-[170px]" />
          <Button variant="outline" onClick={load} disabled={busy}>
            <RefreshCw size={15} className="mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-5">
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <Plus size={16} />
              New Reservation
            </CardTitle>
          </CardHeader>

          <CardContent>
            <form onSubmit={create} className="space-y-4">
              <div>
                <Label>Find Patient</Label>
                <div className="relative mt-1">
                  <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input className="pl-9" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone / patient no." />
                </div>

                <div className="mt-2 max-h-48 overflow-auto rounded-md border border-slate-200 divide-y">
                  {filteredPatients.map((p) => (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => set("patient_id", p.id)}
                      className={`w-full text-left p-2 hover:bg-slate-50 ${form.patient_id === p.id ? "bg-blue-50" : ""}`}
                    >
                      <div className="font-medium text-sm text-slate-900">{p.full_name}</div>
                      <div className="text-xs text-slate-500">{p.contact_number || p.patient_number || "—"}</div>
                    </button>
                  ))}
                </div>

                {selectedPatient ? (
                  <div className="mt-2 text-xs text-blue-700">
                    Selected: <b>{selectedPatient.full_name}</b>
                  </div>
                ) : null}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Date</Label>
                  <Input type="date" value={form.reservation_date} onChange={(e) => set("reservation_date", e.target.value)} />
                </div>
                <div>
                  <Label>Time</Label>
                  <Input type="time" value={form.reservation_time} onChange={(e) => set("reservation_time", e.target.value)} />
                </div>
              </div>

              <div>
                <Label>Doctor</Label>
                <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.doctor_id} onChange={(e) => set("doctor_id", e.target.value)}>
                  <option value="">Any / Unassigned</option>
                  {doctors.map((d) => (
                    <option key={d.id} value={d.id}>{d.name || d.full_name || d.email}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Branch</Label>
                <select className="w-full h-10 rounded-md border border-slate-200 px-3 text-sm" value={form.branch_id} onChange={(e) => set("branch_id", e.target.value)}>
                  <option value="">Main / Default</option>
                  {branches.map((b) => (
                    <option key={b.id} value={b.id}>{b.branch_name}</option>
                  ))}
                </select>
              </div>

              <div>
                <Label>Reason</Label>
                <Input value={form.reason} onChange={(e) => set("reason", e.target.value)} placeholder="Consultation, follow-up, lab review..." />
              </div>

              <div>
                <Label>Notes</Label>
                <Input value={form.notes} onChange={(e) => set("notes", e.target.value)} />
              </div>

              <Button type="submit" disabled={busy} className="w-full bg-blue-600 hover:bg-blue-700">
                <CalendarDays size={15} className="mr-2" />
                Create Reservation
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock size={16} />
              Reservations for {date}
            </CardTitle>
            <Badge variant="secondary">{reservations.length} total</Badge>
          </CardHeader>

          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 border-b">
                  <tr>
                    <th className="p-3 text-left">Time</th>
                    <th className="p-3 text-left">Patient</th>
                    <th className="p-3 text-left">Reason</th>
                    <th className="p-3 text-left">Status</th>
                    <th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {reservations.map((r) => (
                    <tr key={r.id} className="border-b last:border-0">
                      <td className="p-3 font-semibold text-slate-900 whitespace-nowrap">{r.reservation_time}</td>
                      <td className="p-3">
                        <div className="font-medium text-slate-900">{r.patient_name}</div>
                        <div className="text-xs text-slate-500">{r.patient_contact || "—"}</div>
                      </td>
                      <td className="p-3 text-slate-700">{r.reason || "—"}</td>
                      <td className="p-3">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${statusBadge(r.status)}`}>
                          {String(r.status || "").replaceAll("_", " ")}
                        </span>
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        {r.status !== "confirmed" && r.status !== "arrived" ? (
                          <Button size="sm" variant="outline" onClick={() => confirmPayment(r)} className="mr-1">
                            <CheckCircle2 size={14} className="mr-1" />
                            Confirm
                          </Button>
                        ) : null}

                        {r.status !== "arrived" ? (
                          <Button size="sm" variant="outline" onClick={() => arrive(r)} className="mr-1 text-emerald-700">
                            <UserCheck size={14} className="mr-1" />
                            Arrived
                          </Button>
                        ) : null}

                        {r.status !== "cancelled" && r.status !== "arrived" ? (
                          <Button size="sm" variant="outline" onClick={() => updateStatus(r, "cancelled")} className="text-red-700">
                            <AlertTriangle size={14} className="mr-1" />
                            Cancel
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}

                  {reservations.length === 0 ? (
                    <tr>
                      <td colSpan="5" className="p-10 text-center text-slate-400">
                        No reservations for this date.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
